import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_MY_USER_MEDIA, GET_MY_USER_MEDIA } from '@/queries/queries';

// Backoff before each retry (2s, then 8s) — two retries beyond the initial
// attempt, three tries total.
const RETRY_DELAYS_MS = [2000, 8000];

/** Register an uploaded file in the user's media library (fire-and-forget:
 *  a failed registration must never break the upload flow that triggered
 *  it). Retries up to RETRY_DELAYS_MS.length more times with backoff before
 *  giving up.
 *
 *  WHY retry at all: by the time this runs the file already sits in S3 —
 *  registration is the only thing that tells the storage reaper "this
 *  object is spoken for". A registration call that's lost to a transient
 *  network blip (rather than a real rejection) leaves the upload with no
 *  user_medias row, and the reaper may eventually delete the S3 object out
 *  from under the user even though the upload itself succeeded (see
 *  CLAUDE.md §Personal Media Library). Retrying narrows that window; it
 *  does not close it — a failure that persists past the final retry still
 *  logs and gives up, silently, by design.
 *
 *  The cache write keeps pickers filtering by the same kind fresh without a
 *  refetch. */
export function useRegisterUserMedia() {
  const [createMedia] = useMutation(CREATE_MY_USER_MEDIA);
  return useCallback(
    (media: {
      url: string;
      kind: 'image' | 'video' | 'audio';
      name?: string;
      width?: number;
      height?: number;
      duration?: number;
    }) => {
      const attempt = (attemptIndex: number) => {
        createMedia({
          variables: { input: media },
          update(cache, { data }) {
            const created = data?.createMyUserMedia;
            if (!created) return;
            for (const variables of [{ kind: media.kind }, {}]) {
              try {
                const existing = cache.readQuery<{ myUserMedia: { id: string }[] }>({
                  query: GET_MY_USER_MEDIA, variables,
                });
                if (existing?.myUserMedia && !existing.myUserMedia.some((m) => m.id === created.id)) {
                  cache.writeQuery({
                    query: GET_MY_USER_MEDIA, variables,
                    data: { myUserMedia: [created, ...existing.myUserMedia] },
                  });
                }
              } catch { /* query not in cache yet — nothing to update */ }
            }
          },
        }).catch((err) => {
          const delay = RETRY_DELAYS_MS[attemptIndex];
          if (delay !== undefined) {
            setTimeout(() => attempt(attemptIndex + 1), delay);
          } else {
            console.error('Failed to register user media after retries', err);
          }
        });
      };
      attempt(0);
    },
    [createMedia],
  );
}
